export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  'aria-label'?: string
}

export function Toggle({ checked, onChange, label, disabled, ...rest }: ToggleProps) {
  return (
    <label className="inline-flex select-none items-center gap-2 text-[13px] text-mid">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={rest['aria-label'] ?? label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          // Visual findings item 2: was 14x26 — README §7/§7b specify
          // 22x40. Track is `border-box` (Tailwind preflight), so this
          // 22h/40w border-box size leaves a 20x38 CONTENT box once the
          // 1px `border-subtle` on all four sides is subtracted — that
          // content box, not the border box, is what the knob below is
          // centred against.
          'relative h-[22px] w-[40px] rounded-full border border-subtle transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          // Deliberately `bg-elevated`, not `bg-card`, in the unchecked
          // state: a toggle track is chrome, not card content.
          checked ? 'bg-cc-blue' : 'bg-elevated',
        ].join(' ')}
      >
        <span
          className={[
            // Knob centred against the CONTENT box (20x38), not the 22x40
            // border box the old 10px-knob-at-top-0.5 bug measured against
            // (top-0.5 is 2px, which on the old 12px content box left a
            // 2px gap only at the top and none at the bottom — the
            // "knocked down" knob). 18px continues this component's own
            // original 1px-padding proportion (old: 10px knob in a 12px
            // content box, (12-10)/2 = 1px each side) scaled to the new
            // 20px content height: (20-18)/2 = 1px top and bottom, hence
            // `top-px`. Horizontally, the same 1px padding against the
            // 38px content width gives left=1px unchecked and
            // left=38-18-1=19px checked.
            'absolute top-px h-[18px] w-[18px] rounded-full bg-card transition-[left] duration-sidebar',
            checked ? 'left-[19px]' : 'left-px',
          ].join(' ')}
        />
      </button>
      {label ? <span>{label}</span> : null}
    </label>
  )
}
